<template>
  <div :class="hideShell ? '' : 'p-3 plan-tab-shell'">
    <template v-if="!hideShell">
    <div class="d-flex justify-content-end mb-3 px-2 plan-tab-toolbar">
      <button class="btn btn-sm btn-outline-secondary mb-0" style="font-size:0.8rem;" @click="openAddModal()">
        <i class="fas fa-plus me-1"></i> Add Task
      </button>
    </div>
    <div class="px-2 plan-tab-table-wrap">
      <div v-if="loading" class="text-center py-5">
        <i class="fas fa-spinner fa-spin fa-lg text-secondary"></i>
        <p class="text-secondary text-sm mt-2 mb-0">Loading...</p>
      </div>
      <div v-else-if="loadError" class="text-center py-5">
        <i class="fas fa-exclamation-circle fa-2x text-danger opacity-8"></i>
        <p class="text-danger text-sm mt-2 mb-2">{{ loadError }}</p>
        <button type="button" class="btn btn-sm btn-outline-secondary mb-0" @click="loadItems">Retry</button>
      </div>
      <div v-else class="plan-table plan-tab-table">
        <div class="plan-table-header plan-tab-table-header">
          <span class="col-name">TASK NAME</span>
          <span class="col-sub-task text-center">SUB TASK</span>
          <span class="col-date text-center">START DATE</span>
          <span class="col-date text-center">DUE DATE</span>
          <span class="col-status text-center">Status</span>
          <span class="col-action text-center">Actions</span>
        </div>
        <div class="plan-tab-table-body">
        <div v-if="items.length === 0" class="pm-empty-table-row">
          <i class="fas fa-inbox fa-2x text-secondary opacity-5"></i>
          <p class="text-secondary text-sm mt-2 mb-0">No task items yet. Click "+ Add Task" to create one.</p>
        </div>
        <div v-for="item in items" :key="item.testPlanId" class="task-group">
          <div class="plan-row">
            <div class="col-name" style="cursor:pointer;" @click="openDetail(item)">
              <p class="plan-name mb-0 plan-name-link">
                <span class="task-type-prefix">{{ ownerTypeLabel(taskOwnerType(item)) }}</span> : {{ item.testPlanName }}
              </p>
              <div v-if="item.testRunId" class="tp-run-link mt-1" :title="item.linkedTestRunName || 'Test Run'">
                <i class="fas fa-link me-1"></i>{{ item.linkedTestRunName || "Test Run" }}
              </div>
              <div v-if="item.qaOwners && item.qaOwners.length > 0" class="d-flex flex-wrap gap-1 mt-1">
                <span v-for="owner in item.qaOwners" :key="owner.userId" class="qa-badge assignee-badge bo-assignee-pill">{{ formatAssignedName(owner.fullName) }}</span>
              </div>
            </div>
            <div class="col-sub-task text-center">
              <span class="text-sm text-secondary">{{ subTaskCount(item) }}</span>
            </div>
            <div class="col-date text-center">
              <span class="text-sm text-secondary">{{ formatDate(item.planOverallStart) }}</span>
            </div>
            <div class="col-date text-center">
              <span class="text-sm text-secondary">{{ formatDate(item.planOverallDue) }}</span>
            </div>
            <div class="col-status" @click.stop>
              <base-select
                size="sm"
                :model-value="item.testPlanStatus || 'to_do'"
                :options="taskStatusOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select status —"
                searchable
                search-placeholder="Search status…"
                empty-label="No status found"
                :disabled="!!item.testRunId"
                @update:model-value="(v) => onListTaskStatusChange(item, v)"
              />
            </div>
            <div class="col-action text-center">
              <button class="btn btn-link btn-sm p-0 action-edit me-2" title="Edit" @click="openEdit(item)">
                <i class="fas fa-edit" style="font-size:0.85rem;"></i>
              </button>
              <button class="btn btn-link btn-sm p-0 action-delete" title="Delete" @click="remove(item)">
                <i class="fas fa-trash-alt" style="font-size:0.85rem;"></i>
              </button>
            </div>
          </div>
          <div
            v-for="(subTask, subIndex) in normalizeSubTasks(item.subTasks)"
            :key="`sub-row-${item.testPlanId}-${subIndex}`"
            class="subtask-row"
          >
            <div class="col-name" style="cursor:pointer;" @click="openSubTaskDetail(item, subIndex)">
              <div class="subtask-name-wrap">
                <p class="subtask-name mb-0 subtask-name-link">{{ subTask.name || "-" }}</p>
                <div class="mt-1">
                  <span class="qa-badge assignee-badge subtask-assignee-badge bo-assignee-pill">{{ subTaskAssigneeName(subTask.assigneeUserId) }}</span>
                </div>
              </div>
            </div>
            <div class="col-sub-task text-center"></div>
            <div class="col-date text-center">
              <span class="text-sm text-secondary">{{ formatDate(subTask.startDate) }}</span>
            </div>
            <div class="col-date text-center">
              <span class="text-sm text-secondary">{{ formatDate(subTask.dueDate) }}</span>
            </div>
            <div class="col-status" @click.stop>
              <base-select
                size="sm"
                :model-value="subTask.status || 'to_do'"
                :options="taskStatusOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select status —"
                searchable
                search-placeholder="Search status…"
                empty-label="No status found"
                @update:model-value="(v) => onListSubTaskStatusChange(item, subIndex, v)"
              />
            </div>
            <div class="col-action text-center">
              <button class="btn btn-link btn-sm p-0 action-edit me-2" title="Edit Sub Task" @click="openEditSubTask(item, subIndex)">
                <i class="fas fa-edit" style="font-size:0.85rem;"></i>
              </button>
              <button class="btn btn-link btn-sm p-0 action-delete" title="Delete Sub Task" @click="removeSubTaskFromRow(item, subIndex)">
                <i class="fas fa-trash-alt" style="font-size:0.85rem;"></i>
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
    </template>

    <!-- Modal: Add / Edit Task -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-overlay">
        <div class="modal-box modal-box-edit" :class="{ 'modal-box-edit--simple': !showTaskSidePanel }">
          <div class="d-flex align-items-center justify-content-between p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">{{ modalTitle }}</h6>
            <button
              v-if="formReadonly && !viewOnly"
              class="btn btn-sm btn-outline-secondary mb-0"
              style="font-size:0.75rem;"
              @click="enableEditMode"
            >
              <i class="fas fa-edit me-1"></i> Edit
            </button>
          </div>
          <div class="modal-split-body">
            <div class="modal-pane-form" :class="{ 'modal-pane-form--readonly': formReadonly }">
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">{{ isSubTaskDetailView ? 'Sub Task Name' : 'Task Name' }} <span v-if="!formReadonly" class="text-danger">*</span></label>
              <input v-model="form.testPlanName" type="text" class="form-control form-control-sm" :placeholder="isSubTaskDetailView ? 'Enter sub task name' : 'Enter task name'" :disabled="formReadonly" />
            </div>
            <div class="row">
              <div class="col-6 mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Start Date</label>
                <input v-model="form.planOverallStart" type="date" class="form-control form-control-sm" :disabled="formReadonly" />
                <label class="text-xs font-weight-bold mb-1 d-block mt-3">Status</label>
                <base-select
                  size="sm"
                  :model-value="form.status"
                  :options="taskStatusOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="— Select status —"
                  searchable
                  search-placeholder="Search status…"
                  empty-label="No status found"
                  :disabled="viewOnly || isStatusFromTestRun"
                  @update:model-value="onModalStatusChange"
                />
                <p v-if="form.testRunId" class="text-xs text-secondary mb-0 mt-1">
                  Status follows linked test run
                  <span v-if="linkedTestRunStatusLabel"> ({{ linkedTestRunStatusLabel }})</span>.
                </p>
              </div>
              <div class="col-6 mb-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Due Date</label>
                <input v-model="form.planOverallDue" type="date" class="form-control form-control-sm" :disabled="formReadonly" />
              </div>
            </div>
            <!-- Multi-select QA dropdown -->
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Owner Task</label>
              <div class="position-relative" ref="qaDropdownRef">
                <div
                  class="form-control form-control-sm d-flex flex-wrap gap-1 align-items-center"
                  :style="{ minHeight: '31px', cursor: formReadonly ? 'default' : 'pointer' }"
                  @click="!formReadonly && (showQaDropdown = !showQaDropdown)"
                >
                  <span v-if="form.userIds.length === 0" class="text-secondary text-xs">— Select QA —</span>
                  <span
                    v-for="uid in form.userIds"
                    :key="uid"
                    class="qa-badge"
                  >
                    {{ userNameById(uid) }}
                    <span v-if="!formReadonly" class="ms-1" style="cursor:pointer;" @click.stop="removeQaOwner(uid)">×</span>
                  </span>
                </div>
                <div v-if="showQaDropdown && !formReadonly" class="border rounded bg-white shadow-sm position-absolute w-100" style="z-index:9999; max-height:180px; overflow-y:auto; top:100%;">
                  <div
                    v-for="u in users"
                    :key="u.userId"
                    class="px-3 py-2 d-flex align-items-center gap-2"
                    style="cursor:pointer; font-size:0.8rem;"
                    :style="form.userIds.includes(Number(u.userId)) ? 'background:#f0f9ff;' : ''"
                    @click.stop="toggleQaOwner(Number(u.userId))"
                  >
                    <i class="fas text-xs me-1" :class="form.userIds.includes(Number(u.userId)) ? 'fa-check-square text-info' : 'fa-square text-secondary'"></i>
                    {{ formatUserDisplayName(u) }}
                  </div>
                  <div v-if="users.length === 0" class="px-3 py-2 text-secondary text-xs">No users found</div>
                </div>
              </div>
            </div>
            <div v-if="!isSubTaskDetailView" class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Owner Type</label>
              <base-select
                size="sm"
                :model-value="form.ownerType || null"
                :options="ownerTypeOptionList"
                option-label="label"
                option-value="value"
                placeholder="— Select —"
                :disabled="formReadonly"
                @update:model-value="(v) => { form.ownerType = v == null ? '' : v; }"
              />
            </div>
            <div class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Details</label>
              <textarea v-model="form.details" class="form-control form-control-sm" rows="3" placeholder="Enter details..." style="font-size:0.8rem; resize:vertical;" :disabled="formReadonly"></textarea>
            </div>
            <div v-if="!isSubTaskDetailView" class="mb-3">
              <label class="text-xs font-weight-bold mb-1 d-block">Link to Test Run</label>
              <base-select
                size="sm"
                :model-value="form.testRunId"
                :options="testRunOptions"
                option-label="label"
                option-value="value"
                placeholder="— Select test run —"
                :disabled="formReadonly || testRunsLoading"
                @update:model-value="onTestRunLinkChange"
              />
              <p v-if="form.testRunId" class="text-xs text-secondary mb-0 mt-1">
                Status follows linked test run
                <span v-if="linkedTestRunStatusLabel"> ({{ linkedTestRunStatusLabel }})</span>.
              </p>
            </div>
            <div v-if="!isSubTaskDetailView && (!formReadonly || form.subTasks.length > 0)" class="mb-3">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <label class="text-xs font-weight-bold mb-0">Sub Task</label>
                <button v-if="!formReadonly" type="button" class="btn btn-sm btn-outline-secondary mb-0" style="font-size:0.72rem;" @click="addSubTask">
                  <i class="fas fa-plus me-1"></i>Add Sub Task
                </button>
              </div>
              <div v-if="form.subTasks.length === 0" class="text-secondary text-xs">
                No sub task yet
              </div>
              <div v-else class="d-flex flex-column gap-2">
                <div v-for="(subTask, idx) in form.subTasks" :key="`subtask-${idx}`" class="tp-subtask-row">
                  <input
                    v-model="subTask.name"
                    type="text"
                    class="form-control form-control-sm tp-subtask-row__name"
                    :placeholder="`Sub task ${idx + 1}`"
                    :disabled="formReadonly"
                  />
                  <base-select
                    size="sm"
                    class="tp-subtask-row__assignee"
                    :model-value="subTask.assigneeUserId"
                    :options="users.map((u) => ({ value: Number(u.userId), label: formatUserDisplayName(u) }))"
                    option-label="label"
                    option-value="value"
                    placeholder="Assignee"
                    searchable
                    search-placeholder="Search user…"
                    empty-label="No users found"
                    :disabled="formReadonly"
                    @update:model-value="(v) => { subTask.assigneeUserId = v == null ? null : Number(v); }"
                  />
                  <button
                    v-if="!formReadonly"
                    type="button"
                    class="btn btn-link text-danger p-0 mb-0 tp-subtask-row__remove"
                    title="Remove sub task"
                    @click="removeSubTask(idx)"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>
            </div>
            </div>

            <div v-if="showTaskSidePanel" class="tp-modal-side-panel">
              <div class="tp-side-tabs" role="tablist" aria-label="Task side panel">
                <button
                  type="button"
                  class="tp-side-tab"
                  :class="{ active: editModalSideTab === 'remark' }"
                  role="tab"
                  :aria-selected="editModalSideTab === 'remark'"
                  @click="editModalSideTab = 'remark'"
                >
                  Remark &amp; History
                </button>
                <button
                  type="button"
                  class="tp-side-tab"
                  :class="{ active: editModalSideTab === 'jira' }"
                  role="tab"
                  :aria-selected="editModalSideTab === 'jira'"
                  @click="editModalSideTab = 'jira'"
                >
                  <i class="fab fa-jira me-1"></i>Jira
                </button>
              </div>

              <div v-show="editModalSideTab === 'remark'" class="tp-side-tab-panel tp-side-tab-panel--remark">
                <template v-if="editItem">
                  <div v-if="!formReadonly" class="modal-remark-fixed">
                    <label class="text-xs font-weight-bold mb-1 d-block">Remark</label>
                    <MentionTextarea
                      v-model="remarkText"
                      :rows="4"
                      placeholder="Add a remark... (@ to tag)"
                      input-class="form-control form-control-sm mb-2"
                      :input-style="{ fontSize: '0.8rem', resize: 'vertical' }"
                    />
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-dark w-100"
                      :disabled="remarkSaving || !remarkText.trim()"
                      @click="saveRemark"
                    >
                      <span v-if="remarkSaving"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
                      <span v-else>Save Remark</span>
                    </button>
                  </div>

                  <div class="modal-remark-history">
                    <h6 class="text-xs font-weight-bold text-secondary text-uppercase mb-2">History</h6>
                    <div class="activity-history-scroll">
                      <div v-if="activityLoading" class="text-secondary text-xs py-2">Loading...</div>
                      <div v-else-if="activityHistory.length === 0" class="text-secondary text-xs py-2">
                        No history yet.
                      </div>
                      <div v-else class="activity-history-list">
                        <div
                          v-for="entry in activityHistory"
                          :key="entry.activityId"
                          class="activity-history-item"
                        >
                          <div class="d-flex justify-content-between gap-2 mb-1">
                            <span class="activity-badge" :class="entry.entryType === 'remark' ? 'badge-remark' : 'badge-change'">
                              {{ entry.entryType === 'remark' ? 'Remark' : 'Change' }}
                            </span>
                            <span class="text-secondary text-xs text-nowrap">{{ formatActivityTime(entry.createdAt) }}</span>
                          </div>
                          <p class="text-xs text-secondary mb-1">{{ entry.authorName }}</p>
                          <p v-if="entry.entryType === 'remark'" class="text-sm mb-0 activity-text"><MentionText :text="entry.remarkText" /></p>
                          <div v-else-if="isSubTasksHistoryField(entry.fieldName)" class="activity-subtasks-history">
                            <p class="text-sm mb-2"><strong>{{ entry.fieldName }}</strong></p>
                            <div class="activity-subtasks-block">
                              <div class="activity-subtasks-label text-xxs text-secondary text-uppercase mb-1">Before</div>
                              <div v-if="parseSubTasksHistoryJson(entry.oldValue).length === 0" class="text-xs text-secondary">—</div>
                              <div v-else class="table-responsive">
                                <table class="table table-sm mb-0 activity-subtasks-table">
                                  <thead>
                                    <tr>
                                      <th>Name</th>
                                      <th>Status</th>
                                      <th>Assignee</th>
                                      <th>Start</th>
                                      <th>Due</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(st, stIdx) in parseSubTasksHistoryJson(entry.oldValue)" :key="'old-' + stIdx">
                                      <td>{{ st.name || '—' }}</td>
                                      <td>{{ statusLabel(st.status) }}</td>
                                      <td>{{ subTaskHistoryAssigneeLabel(st) }}</td>
                                      <td>{{ formatDate(st.startDate) }}</td>
                                      <td>{{ formatDate(st.dueDate) }}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                            <div class="activity-subtasks-block mt-2">
                              <div class="activity-subtasks-label text-xxs text-secondary text-uppercase mb-1">After</div>
                              <div v-if="parseSubTasksHistoryJson(entry.newValue).length === 0" class="text-xs text-secondary">—</div>
                              <div v-else class="table-responsive">
                                <table class="table table-sm mb-0 activity-subtasks-table">
                                  <thead>
                                    <tr>
                                      <th>Name</th>
                                      <th>Status</th>
                                      <th>Assignee</th>
                                      <th>Start</th>
                                      <th>Due</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    <tr v-for="(st, stIdx) in parseSubTasksHistoryJson(entry.newValue)" :key="'new-' + stIdx">
                                      <td>{{ st.name || '—' }}</td>
                                      <td>{{ statusLabel(st.status) }}</td>
                                      <td>{{ subTaskHistoryAssigneeLabel(st) }}</td>
                                      <td>{{ formatDate(st.startDate) }}</td>
                                      <td>{{ formatDate(st.dueDate) }}</td>
                                    </tr>
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </div>
                          <p v-else class="text-sm mb-0 activity-text">
                            <strong>{{ entry.fieldName }}</strong>:
                            <span class="text-secondary">{{ formatHistoryValue(entry.fieldName, entry.oldValue) }}</span>
                            →
                            <span>{{ formatHistoryValue(entry.fieldName, entry.newValue) }}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
                <div v-else class="text-secondary text-xs py-3">
                  Save the task first to add remarks and view history.
                </div>
              </div>

              <div v-show="editModalSideTab === 'jira'" class="tp-side-tab-panel">
                <div v-if="!formReadonly" class="d-flex align-items-center justify-content-end gap-2 mb-2">
                  <div class="tp-jira-actions">
                    <button type="button" class="btn btn-outline-primary btn-sm mb-0" @click="openJiraActionModal('link', 'task')">
                      <i class="fas fa-link me-1"></i>Link
                    </button>
                    <button type="button" class="btn btn-outline-info btn-sm mb-0" @click="openJiraActionModal('createTask', 'task')">
                      <i class="fab fa-jira me-1"></i>Task
                    </button>
                  </div>
                </div>
                <div v-if="modalJiraLinksLoading" class="text-xs text-secondary py-2">
                  <i class="fas fa-spinner fa-spin me-1"></i>Loading…
                </div>
                <div v-else-if="!modalJiraRows.length" class="text-xs text-secondary py-2">No Jira linked to this task</div>
                <div v-else class="tp-jira-table-wrap">
                  <table class="table table-sm mb-0 tp-jira-table">
                    <thead>
                      <tr>
                        <th class="tp-jira-col-id">ID</th>
                        <th class="tp-jira-col-title">Title</th>
                        <th class="tp-jira-col-source">For</th>
                        <th class="tp-jira-col-status">Status</th>
                        <th v-if="!formReadonly" class="text-center tp-jira-col-action">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="row in modalJiraRows" :key="row.rowKey">
                        <td class="tp-jira-col-id">
                          <a :href="row.url" target="_blank" rel="noopener" class="text-xs font-weight-bold">{{ row.key }}</a>
                        </td>
                        <td class="tp-jira-col-title text-xs text-secondary text-truncate" :title="row.summary || ''">{{ row.summary || "—" }}</td>
                        <td class="tp-jira-col-source text-xs text-secondary text-truncate" :title="row.sourceLabel">{{ row.sourceLabel }}</td>
                        <td class="tp-jira-col-status">
                          <jira-status-select
                            :issue-key="row.key"
                            :status="row.status || ''"
                            :transitions="row.transitions || []"
                            compact
                            mini
                            :disabled="formReadonly"
                            @update:status="row.status = $event"
                            @update:transitions="row.transitions = $event"
                          />
                        </td>
                        <td v-if="!formReadonly" class="text-center tp-jira-col-action">
                          <button type="button" class="btn btn-link btn-sm p-0 action-delete" title="Remove link" @click="removeModalJiraLink(row)">
                            <i class="fas fa-trash-alt" style="font-size:0.85rem;"></i>
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div v-if="jiraCreateError" class="text-danger text-xs mt-2">{{ jiraCreateError }}</div>
              </div>
            </div>
          </div>
          <div class="p-3 border-top">
            <div v-if="saveError" class="alert alert-danger py-2 px-3 mb-2 text-xs">{{ saveError }}</div>
            <div v-if="jiraWarning" class="alert alert-warning py-2 px-3 mb-2 text-xs">
              <div class="font-weight-bold mb-1"><i class="fab fa-jira me-1"></i> Plan saved — แต่ Jira subtask สร้างไม่สำเร็จ:</div>
              <pre class="mb-0" style="font-size:0.7rem;white-space:pre-wrap;color:#856404;">{{ jiraWarning }}</pre>
              <div class="mt-1 text-secondary" style="font-size:0.68rem;">หมายเหตุ: Jira Subtask สามารถสร้างได้เฉพาะใต้ Story เท่านั้น ไม่สามารถสร้างใต้ Epic ได้</div>
            </div>
            <div class="d-flex align-items-center justify-content-end gap-2">
              <template v-if="formReadonly">
                <button class="btn btn-sm btn-outline-secondary mb-0" @click="closeModal">Close</button>
                <button v-if="!viewOnly" class="btn btn-sm bg-gradient-dark text-white mb-0" @click="enableEditMode">
                  <i class="fas fa-edit me-1"></i>Edit
                </button>
              </template>
              <template v-else>
                <button class="btn btn-sm btn-outline-secondary mb-0" @click="closeModal">Cancel</button>
                <button class="btn btn-sm bg-gradient-dark text-white mb-0" @click="save" :disabled="isSaving">
                  <span v-if="isSaving"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
                  <span v-else>Save</span>
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Jira link / create modal -->
    <Teleport to="body">
      <div v-if="showJiraActionModal" class="modal-overlay" style="z-index:3100;">
        <div class="modal-box" style="width:480px;max-height:none;">
          <div class="d-flex align-items-center p-3 border-bottom">
            <h6 class="mb-0 font-weight-bold">{{ jiraActionModalTitle }}</h6>
          </div>
          <div class="p-3">
            <label class="text-xs font-weight-bold mb-1 d-block">Jira Space <span class="text-danger">*</span></label>
            <base-select
              size="sm"
              v-model="jiraActionProjectKey"
              :options="jiraActionProjectOptions"
              option-label="label"
              option-value="value"
              placeholder="— Select Space —"
              @change="onJiraActionProjectChange"
            />
            <span v-if="jiraLoadingProjects" class="text-xs text-secondary mt-1 d-block">
              <i class="fas fa-spinner fa-spin me-1"></i>Loading spaces...
            </span>
            <template v-if="jiraActionIsCreate">
              <div class="mt-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Issue Title <span class="text-danger">*</span></label>
                <input v-model="jiraActionTitle" type="text" class="form-control form-control-sm" placeholder="หัวข้อ issue" />
              </div>
              <div class="mt-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Description <span class="text-danger">*</span></label>
                <textarea v-model="jiraActionDescription" class="form-control form-control-sm" rows="4" placeholder="รายละเอียด issue" style="resize:vertical;"></textarea>
              </div>
              <div class="mt-3">
                <label class="text-xs font-weight-bold mb-1 d-block">Assignee</label>
                <base-select
                  size="sm"
                  v-model="jiraActionAssigneeAccountId"
                  :options="jiraActionAssigneeOptions"
                  option-label="label"
                  option-value="value"
                  placeholder="— ไม่ระบุ —"
                  :disabled="!jiraActionProjectKey || jiraLoadingAssignable"
                />
                <span v-if="jiraLoadingAssignable" class="text-xs text-secondary mt-1 d-block">
                  <i class="fas fa-spinner fa-spin me-1"></i>Loading users…
                </span>
              </div>
            </template>
            <div v-if="jiraActionProjectKey" class="mt-3">
              <label class="text-xs font-weight-bold mb-1 d-block">
                Jira Tickets
                <span v-if="jiraActionMode === 'link'" class="text-danger">*</span>
                <span v-else class="text-secondary font-weight-normal">(optional — relate to parent)</span>
              </label>
              <div v-if="jiraActionIssueKeys.length" class="d-flex flex-wrap gap-1 mb-2">
                <span v-for="key in jiraActionIssueKeys" :key="key" class="jira-tag">
                  <i class="fab fa-jira me-1"></i>{{ key }}
                  <button type="button" class="jira-tag-remove" @click="removeJiraActionTicket(key)">×</button>
                </span>
              </div>
              <input v-model="jiraActionSearch" type="text" class="form-control form-control-sm mb-1" placeholder="Search ticket..." @input="onJiraActionSearch" />
              <div class="jira-issue-list">
                <div v-if="jiraLoadingIssues" class="text-center py-2 text-secondary text-xs">
                  <i class="fas fa-spinner fa-spin me-1"></i>Loading...
                </div>
                <div v-else-if="jiraIssues.length === 0" class="text-center py-2 text-secondary text-xs">No tickets found</div>
                <div v-for="i in jiraIssues" :key="`action-${i.key}`"
                  class="jira-issue-item"
                  :class="{ 'jira-issue-selected': jiraActionIssueKeys.includes(i.key) }"
                  @click="toggleJiraActionTicket(i.key)">
                  <i class="fas fa-check-circle me-1 text-success" v-if="jiraActionIssueKeys.includes(i.key)"></i>
                  <i class="fas fa-circle me-1" style="font-size:0.5rem; color:transparent;" v-else></i>
                  <span class="text-xs font-weight-bold me-1">{{ i.key }}</span>
                  <span class="text-xs">{{ i.summary }}</span>
                  <span v-if="i.status" class="text-xs text-secondary ms-1">({{ i.status }})</span>
                </div>
              </div>
            </div>
            <div v-if="jiraActionError" class="text-danger text-xs mt-2">{{ jiraActionError }}</div>
          </div>
          <div class="d-flex justify-content-end gap-2 p-3 border-top">
            <button type="button" class="btn btn-sm btn-outline-secondary mb-0" :disabled="jiraCreatingIssue" @click="closeJiraActionModal">Cancel</button>
            <button type="button" class="btn btn-sm bg-gradient-dark text-white mb-0" :disabled="jiraActionConfirmDisabled" @click="confirmJiraAction">
              <span v-if="jiraCreatingIssue"><i class="fas fa-spinner fa-spin me-1"></i>Working...</span>
              <span v-else>{{ jiraActionConfirmLabel }}</span>
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { API_BASE_URL, apiHeaders, fetchWithTimeout } from "@/config/apiBase";
import { testPlanItemsApi, usersApi, testRunsApi } from "@/services/backendApi";
import {
  TEST_PLAN_OWNER_TYPE_OPTIONS,
  TEST_PLAN_TASK_STATUS_OPTIONS,
  testPlanTaskStatusLabel,
  testPlanTaskStatusPillClass,
  taskStatusFromTestRunStatus,
} from "@/utils/testPlanTaskStatus";
import BaseSelect from "@/components/BaseSelect.vue";
import JiraStatusSelect from "@/components/JiraStatusSelect.vue";
import MentionText from "@/components/MentionText.vue";
import MentionTextarea from "@/components/MentionTextarea.vue";
import { enrichJiraLinkRows, fetchJiraIssuesByKeys } from "@/services/jiraStatusApi";
import { parseJiraLinks } from "@/utils/jiraLinksPayload";
import { teamsOverlap, userHasAllTeams } from "@/utils/userTeams";

export default {
  name: "test-plan-tab",
  components: { BaseSelect, JiraStatusSelect, MentionText, MentionTextarea },
  props: {
    viewOnly: { type: Boolean, default: false },
    hideShell: { type: Boolean, default: false },
    openTaskId: { type: [Number, String], default: null },
  },
  emits: ["close"],
  data() {
    return {
      ownerTypeOptionList: TEST_PLAN_OWNER_TYPE_OPTIONS,
      loading: false,
      loadError: null,
      isSaving: false,
      showModal: false,
      showQaDropdown: false,
      modalMode: "add",
      editItem: null,
      items: [],
      users: [],
      form: { testPlanName: "", planOverallStart: "", planOverallDue: "", userIds: [], ownerType: "qa", status: "to_do", details: "", subTasks: [], taskJiraLinks: [], testRunId: null },
      testRuns: [],
      testRunsLoading: false,
      subProjectJiraLinks: [],
      jiraProjects: [],
      jiraIssues: [],
      jiraLoadingProjects: false,
      jiraLoadingIssues: false,
      jiraSearch: "",
      jiraSearchTimer: null,
      saveError: null,
      jiraWarning: null,
      jiraCreateError: null,
      modalJiraLinksLoading: false,
      showJiraActionModal: false,
      jiraActionMode: null,
      jiraActionContext: null,
      jiraActionProjectKey: "",
      jiraActionIssueKeys: [],
      jiraActionSearch: "",
      jiraActionSearchTimer: null,
      jiraActionTitle: "",
      jiraActionDescription: "",
      jiraActionAssigneeAccountId: "",
      jiraAssignableUsers: [],
      jiraLoadingAssignable: false,
      jiraActionError: null,
      jiraCreatingIssue: false,
      remarkText: "",
      remarkSaving: false,
      activityHistory: [],
      editBaseline: null,
      activityLoading: false,
      editModalSideTab: "remark",
      subTaskDetailContext: null,
    };
  },
  computed: {
    formReadonly() {
      return this.viewOnly || this.modalMode === "detail";
    },
    showTaskSidePanel() {
      return this.modalMode !== "add";
    },
    isSubTaskDetailView() {
      return !!this.subTaskDetailContext;
    },
    modalTitle() {
      if (this.isSubTaskDetailView) {
        if (this.modalMode === "detail") return "Sub Task Detail";
        return "Edit Sub Task";
      }
      if (this.modalMode === "detail") return "Task Detail";
      if (this.modalMode === "edit") return "Edit Task";
      return "Add Task";
    },
    taskStatusOptions() {
      return TEST_PLAN_TASK_STATUS_OPTIONS;
    },
    jiraActionProjectOptions() {
      return (this.jiraProjects || []).map((p) => ({
        label: `${p.name} (${p.key})`,
        value: p.key,
      }));
    },
    jiraActionAssigneeOptions() {
      return (this.jiraAssignableUsers || []).map((u) => ({
        label: u.displayName,
        value: u.accountId,
      }));
    },
    userTeamSource() {
      const user = this.currentUser;
      return user?.teams?.length ? user.teams : user?.team;
    },
    linkableTestRuns() {
      const selectedId = this.form.testRunId != null ? Number(this.form.testRunId) : null;
      const allowAllTeams = userHasAllTeams(this.userTeamSource);
      return (this.testRuns || []).filter((run) => {
        if (selectedId && Number(run.id) === selectedId) return true;
        if (this.isDoneTestRunStatus(run.status)) return false;
        if (allowAllTeams) return true;
        const runTeam = run.teamTrimmed || run.team || run.owningTeam;
        return teamsOverlap(this.userTeamSource, runTeam);
      });
    },
    testRunOptions() {
      return [
        { value: null, label: "— None —" },
        ...this.linkableTestRuns.map((r) => ({
          value: r.id,
          label: r.name || `Test Run ${r.id}`,
        })),
      ];
    },
    isStatusFromTestRun() {
      return !!this.form.testRunId;
    },
    linkedTestRunStatusLabel() {
      const run = this.testRuns.find((r) => r.id === this.form.testRunId);
      return run?.status || "";
    },
    currentUserId() {
      const raw = this.$store?.state?.user?.userId;
      return raw != null ? Number(raw) : null;
    },
    taskJiraPanelKeys() {
      return (this.form.taskJiraLinks || []).map((l) => l.key).filter(Boolean);
    },
    modalJiraRows() {
      const rows = [];
      (this.form.taskJiraLinks || []).forEach((j) => {
        rows.push({
          ...j,
          rowKey: `task-${j.key}`,
          sourceType: "task",
          sourceIndex: null,
          sourceLabel: "Task",
        });
      });
      (this.form.subTasks || []).forEach((st, idx) => {
        this.getSubTaskJiraLinks(st).forEach((j) => {
          rows.push({
            ...j,
            rowKey: `subtask-${idx}-${j.key}`,
            sourceType: "subtask",
            sourceIndex: idx,
            sourceLabel: st.name?.trim() || `Sub Task ${idx + 1}`,
          });
        });
      });
      return rows;
    },
    jiraActionIsCreate() {
      return this.jiraActionMode === "createTask";
    },
    jiraActionModalTitle() {
      if (this.jiraActionMode === "createTask") return "Create Jira Task";
      return "Link Jira";
    },
    jiraActionConfirmLabel() {
      if (this.jiraActionMode === "createTask") return "Create Task";
      return "Link";
    },
    jiraActionConfirmDisabled() {
      if (this.jiraCreatingIssue) return true;
      if (!this.jiraActionProjectKey) return true;
      if (this.jiraActionMode === "link") return !this.jiraActionIssueKeys.length;
      if (!String(this.jiraActionTitle || "").trim()) return true;
      if (!String(this.jiraActionDescription || "").trim()) return true;
      return false;
    },
    currentUser() {
      return this.$store?.state?.user || null;
    },
  },
  watch: {
    showModal(isOpen) {
      if (isOpen) this.editModalSideTab = "remark";
    },
    openTaskId: {
      immediate: true,
      handler(id) {
        if (this.hideShell) this.openStandaloneTask(id);
      },
    },
  },
  async created() {
    if (this.hideShell) {
      await this.loadUsers();
    } else {
      await Promise.all([this.loadItems(), this.loadUsers(), this.loadSubProjectJiraLinks()]);
    }
    document.addEventListener("click", this.closeQaDropdown);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.closeQaDropdown);
  },
  methods: {
    projectPlanId() {
      return this.$route.params.id;
    },
    async loadItems() {
      this.loading = true;
      this.loadError = null;
      try {
        this.items = await testPlanItemsApi.getByProjectPlan(this.projectPlanId());
        await this.updateParentTestPlanStatus();
      } catch (e) {
        console.error("loadItems error:", e);
        this.items = [];
        this.loadError = e.message || "Failed to load tasks.";
      } finally {
        this.loading = false;
      }
    },
    async loadUsers() {
      try { this.users = await usersApi.getAll(); } catch (e) { console.error(e); }
    },
    async loadSubProjectJiraLinks() {
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/project-plan/${this.projectPlanId()}`);
        const data = await res.json();
        const raw = data.projectLink ? String(data.projectLink).trim() : null;
        if (!raw) return;
        try {
          const parsed = JSON.parse(raw);
          const links = Array.isArray(parsed)
            ? parsed
            : (parsed && Array.isArray(parsed.links) ? parsed.links : []);
          if (links.length) {
            this.subProjectJiraLinks = links;
            await this.enrichSubProjectJiraLinks();
          }
        } catch (_) { /* ignore */ }
      } catch (e) { console.error("loadSubProjectJiraLinks error:", e); }
    },
    async enrichSubProjectJiraLinks() {
      if (!this.subProjectJiraLinks.length) return;
      try {
        const data = await fetchJiraIssuesByKeys(this.subProjectJiraLinks.map((l) => l.key));
        this.subProjectJiraLinks = enrichJiraLinkRows(this.subProjectJiraLinks, data);
      } catch (_) { /* ignore */ }
    },
    closeQaDropdown(e) {
      if (this.$refs.qaDropdownRef && !this.$refs.qaDropdownRef.contains(e.target)) {
        this.showQaDropdown = false;
      }
    },
    toggleQaOwner(uid) {
      const idx = this.form.userIds.indexOf(uid);
      if (idx === -1) this.form.userIds.push(uid);
      else this.form.userIds.splice(idx, 1);
    },
    removeQaOwner(uid) {
      this.form.userIds = this.form.userIds.filter(id => id !== uid);
    },
    userNameById(uid) {
      const u = this.users.find(u => Number(u.userId) === uid);
      return u ? this.formatUserDisplayName(u) : uid;
    },
    formatUserDisplayName(user) {
      const first = String(user?.firstName || "").trim();
      const nick = String(user?.nickname || "").trim();
      if (!first) return "—";
      return nick ? `${first} (${nick})` : first;
    },
    formatAssignedName(rawName) {
      const name = String(rawName || "").trim();
      if (!name) return "—";
      const nicknameMatch = name.match(/\(([^)]+)\)/);
      const firstName = name.replace(/\([^)]*\)/g, " ").trim().split(/\s+/)[0] || "";
      if (nicknameMatch && nicknameMatch[1]) {
        return `${firstName} (${nicknameMatch[1].trim()})`;
      }
      return firstName;
    },
    openAddModal(ownerType = "qa") {
      this.editItem = null;
      this.showQaDropdown = false;
      this.saveError = null;
      this.jiraWarning = null;
      this.jiraCreateError = null;
      this.form = { testPlanName: "", planOverallStart: "", planOverallDue: "", userIds: [], ownerType, status: "to_do", details: "", subTasks: [], taskJiraLinks: [], testRunId: null };
      this.modalMode = "add";
      this.subTaskDetailContext = null;
      this.editModalSideTab = "remark";
      this.showModal = true;
      this.loadJiraProjects();
      this.loadTestRunsForPlan();
    },
    isDoneTestRunStatus(status) {
      const key = String(status || "")
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");
      return key === "done" || key === "completed";
    },
    async loadTestRunsForPlan() {
      this.testRunsLoading = true;
      try {
        this.testRuns = await testRunsApi.getByProject();
      } catch (e) {
        console.error("loadTestRunsForPlan error:", e);
        this.testRuns = [];
      } finally {
        this.testRunsLoading = false;
      }
    },
    onTestRunLinkChange(val) {
      this.form.testRunId = val == null || val === "" ? null : Number(val);
      if (!this.form.testRunId) return;
      const run = this.testRuns.find((r) => Number(r.id) === Number(this.form.testRunId));
      if (run?.status) {
        this.form.status = taskStatusFromTestRunStatus(run.status);
      }
    },
    loadItemIntoForm(item) {
      const ownerType = this.taskOwnerType(item) || "";
      const startDate = item.planOverallStart ? String(item.planOverallStart).substring(0, 10) : "";
      const dueDate = item.planOverallDue ? String(item.planOverallDue).substring(0, 10) : "";
      const userIds = (item.qaOwners || []).map((o) => Number(o.userId));
      const subTasks = this.normalizeSubTasks(item.subTasks);
      const taskJiraLinks = this.parseTaskJiraLinksRaw(item.taskLink || item.task_link);
      this.form = {
        testPlanName: item.testPlanName,
        planOverallStart: startDate,
        planOverallDue: dueDate,
        userIds: [...userIds],
        ownerType,
        status: item.testPlanStatus || "to_do",
        details: item.details || "",
        subTasks: [...subTasks],
        taskJiraLinks: [...taskJiraLinks],
        testRunId: item.testRunId != null ? Number(item.testRunId) : null,
      };
      this.editBaseline = {
        testPlanName: item.testPlanName,
        planOverallStart: startDate,
        planOverallDue: dueDate,
        userIds: [...userIds].sort((a, b) => a - b),
        ownerType,
        status: item.testPlanStatus || "to_do",
        details: item.details || "",
        subTasksJson: JSON.stringify(this.serializeSubTasksForSave(subTasks)),
        taskJiraLinksJson: JSON.stringify(taskJiraLinks),
        testRunId: item.testRunId != null ? Number(item.testRunId) : null,
      };
    },
    openEdit(item) {
      this.subTaskDetailContext = null;
      this.editItem = item;
      this.modalMode = "edit";
      this.showQaDropdown = false;
      this.remarkText = "";
      this.jiraCreateError = null;
      this.loadItemIntoForm(item);
      this.editModalSideTab = "remark";
      this.showModal = true;
      this.loadJiraProjects();
      this.loadTestRunsForPlan();
      this.enrichAllModalJiraLinks();
      this.loadActivityHistory();
    },
    openDetail(item) {
      this.subTaskDetailContext = null;
      this.editItem = item;
      this.modalMode = "detail";
      this.showQaDropdown = false;
      this.remarkText = "";
      this.jiraCreateError = null;
      this.saveError = null;
      this.loadItemIntoForm(item);
      this.editModalSideTab = "remark";
      this.showModal = true;
      this.loadJiraProjects();
      this.loadTestRunsForPlan();
      this.enrichAllModalJiraLinks();
      this.loadActivityHistory();
    },
    loadSubTaskIntoForm(parentItem, subIndex) {
      const list = this.normalizeSubTasks(parentItem?.subTasks);
      const subTask = list[subIndex];
      if (!subTask) return;
      const ownerType = this.taskOwnerType(parentItem) || "qa";
      const startDate = subTask.startDate ? String(subTask.startDate).substring(0, 10) : "";
      const dueDate = subTask.dueDate ? String(subTask.dueDate).substring(0, 10) : "";
      const assigneeUserId = Number.isFinite(Number(subTask.assigneeUserId))
        ? Number(subTask.assigneeUserId)
        : null;
      const taskJiraLinks = this.getSubTaskJiraLinks(subTask).map((l) => ({ ...l }));
      this.form = {
        testPlanName: subTask.name || "",
        planOverallStart: startDate,
        planOverallDue: dueDate,
        userIds: assigneeUserId ? [assigneeUserId] : [],
        ownerType,
        status: subTask.status || "to_do",
        details: subTask.detail || "",
        subTasks: [],
        taskJiraLinks: [...taskJiraLinks],
      };
      this.editBaseline = null;
    },
    openSubTaskMainModal(item, subIndex, mode = "detail") {
      const list = this.normalizeSubTasks(item?.subTasks);
      if (!list[subIndex]) return;
      this.subTaskDetailContext = { parentItem: item, subIndex };
      this.editItem = item;
      this.modalMode = mode;
      this.showQaDropdown = false;
      this.remarkText = "";
      this.jiraCreateError = null;
      this.saveError = null;
      this.loadSubTaskIntoForm(item, subIndex);
      this.editModalSideTab = "remark";
      this.showModal = true;
      this.loadJiraProjects();
      this.enrichAllModalJiraLinks();
      this.loadActivityHistory();
    },
    openSubTaskDetail(item, subIndex) {
      this.openSubTaskMainModal(item, subIndex, "detail");
    },
    enableEditMode() {
      if (this.viewOnly || !this.editItem) return;
      this.modalMode = "edit";
      this.editModalSideTab = "remark";
    },
    async openStandaloneTask(id) {
      const taskId = Number(id);
      if (!Number.isFinite(taskId) || taskId <= 0) return;
      try {
        const item = await testPlanItemsApi.getById(taskId);
        this.openDetail(item);
      } catch (e) {
        console.error("openStandaloneTask error:", e);
        this.editItem = { testPlanId: taskId, testPlanName: `Task #${taskId}` };
        this.modalMode = "detail";
        this.showModal = true;
        this.saveError = e.message || "Failed to load task detail";
      }
    },
    closeModal() {
      this.showModal = false;
      this.showQaDropdown = false;
      this.editItem = null;
      this.modalMode = "add";
      this.subTaskDetailContext = null;
      this.saveError = null;
      this.jiraWarning = null;
      this.jiraCreateError = null;
      this.remarkText = "";
      this.activityHistory = [];
      this.activityLoading = false;
      this.editBaseline = null;
      this.editModalSideTab = "remark";
      this.closeJiraActionModal();
      this.$emit("close");
    },
    async loadActivityHistory() {
      if (!this.editItem?.testPlanId) return;
      this.activityLoading = true;
      try {
        this.activityHistory = await testPlanItemsApi.getActivity(this.editItem.testPlanId);
      } catch (e) {
        console.error("loadActivityHistory error:", e);
        this.activityHistory = [];
      } finally {
        this.activityLoading = false;
      }
    },
    async saveRemark() {
      const text = this.remarkText.trim();
      if (!text || !this.editItem?.testPlanId || this.remarkSaving) return;
      this.remarkSaving = true;
      try {
        this.activityHistory = await testPlanItemsApi.addRemark(this.editItem.testPlanId, {
          remarkText: text,
          actorUserId: this.currentUserId,
        });
        this.remarkText = "";
      } catch (e) {
        console.error("saveRemark error:", e);
        alert(e.message || "Failed to save remark");
      } finally {
        this.remarkSaving = false;
      }
    },
    formatActivityTime(val) {
      if (!val) return "";
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    formatDate(val) {
      if (!val) return "—";
      const s = String(val).substring(0, 10);
      const [y, m, d] = s.split("-");
      return `${d}-${m}-${y}`;
    },
    normalizeSubTasks(raw) {
      const parsed = Array.isArray(raw) ? raw : [];
      return parsed.map((subTask) => ({
        name: subTask?.name || "",
        detail: subTask?.detail || "",
        status: subTask?.status || "to_do",
        startDate: subTask?.startDate || null,
        dueDate: subTask?.dueDate || null,
        assigneeUserId: Number.isFinite(Number(subTask?.assigneeUserId)) ? Number(subTask.assigneeUserId) : null,
        jiraLinks: Array.isArray(subTask?.jiraLinks)
          ? subTask.jiraLinks.map((l) => ({ ...l }))
          : this.parseTaskJiraLinksRaw(subTask?.jiraLink),
      }));
    },
    serializeSubTasksForSave(subTasks) {
      return (subTasks || []).map((subTask) => ({
        name: subTask.name || "",
        detail: subTask.detail || "",
        status: subTask.status || "to_do",
        startDate: subTask.startDate || null,
        dueDate: subTask.dueDate || null,
        assigneeUserId: subTask.assigneeUserId,
        jiraLink: this.serializeTaskJiraLinks(this.getSubTaskJiraLinks(subTask)),
      })).filter((st) => String(st.name || "").trim());
    },
    defaultSubTaskAssignee() {
      const ids = (this.form.userIds || []).map(Number).filter(Number.isFinite);
      return ids.length > 0 ? ids[0] : null;
    },
    addSubTask() {
      this.form.subTasks.push({
        name: "",
        detail: "",
        status: "to_do",
        assigneeUserId: this.defaultSubTaskAssignee(),
        jiraLinks: [],
      });
    },
    removeSubTask(index) {
      this.form.subTasks.splice(index, 1);
    },
    openEditSubTask(item, subIndex) {
      this.openSubTaskMainModal(item, subIndex, "edit");
    },
    subTaskCount(item) {
      const list = this.normalizeSubTasks(item?.subTasks);
      return list.length;
    },
    subTaskAssigneeName(userId) {
      if (!userId) return "Unassigned";
      return this.userNameById(Number(userId));
    },
    async removeSubTaskFromRow(item, subIndex) {
      const list = this.normalizeSubTasks(item?.subTasks);
      if (!Array.isArray(list) || !list[subIndex]) return;
      if (!confirm(`Delete sub task "${list[subIndex].name || '-'}"?`)) return;
      const updatedSubTasks = list.filter((_, idx) => idx !== subIndex);
      try {
        await testPlanItemsApi.update(item.testPlanId, {
          testPlanName: item.testPlanName,
          planOverallStart: item.planOverallStart || null,
          planOverallDue: item.planOverallDue || null,
          actionStartDate: item.actionStartDate || null,
          actionDueDate: item.actionDueDate || null,
          userIds: (item.qaOwners || []).map((owner) => Number(owner.userId)),
          status: item.testPlanStatus || "to_do",
          details: item.details || null,
          ownerType: this.taskOwnerType(item) || null,
          subTasks: updatedSubTasks,
        });
        await this.loadItems();
      } catch (e) {
        console.error("delete sub task error:", e);
        alert(e.message || "Delete sub task failed");
      }
    },
    taskOwnerType(item) {
      if (!item) return null;
      const raw = item.ownerType != null ? item.ownerType : item.owner_type;
      const s = raw != null ? String(raw).trim() : "";
      return s || null;
    },
    ownerTypeLabel(val) {
      const raw = val != null ? String(val).trim() : "";
      if (!raw) return "—";
      const map = {
        qa: "QA",
        dev: "DEV",
        pm: "PM",
        dev_ops: "DEV OPS",
      };
      const key = raw.toLowerCase();
      return map[key] || raw;
    },
    formatHistoryValue(fieldName, value) {
      if (!value || value === "—") return "—";
      if (this.isSubTasksHistoryField(fieldName)) {
        const list = this.parseSubTasksHistoryJson(value);
        if (!list.length) return "—";
        return list.map((st) => `${st.name || "—"} (${this.statusLabel(st.status)})`).join(", ");
      }
      if (fieldName === "Status") {
        const key = String(value).trim().toLowerCase().replace(/\s+/g, "_");
        return this.statusLabel(key) || value;
      }
      if (fieldName === "Owner Type") {
        return this.ownerTypeLabel(value) !== "—" ? this.ownerTypeLabel(value) : value;
      }
      if (fieldName === "Owner Task") {
        return this.formatHistoryUserIds(value);
      }
      return value;
    },
    formatHistoryUserIds(val) {
      if (!val || val === "—") return "—";
      const parts = String(val).split(",").map((s) => s.trim()).filter(Boolean);
      if (!parts.length) return "—";
      const names = parts.map((part) => {
        const id = Number(part);
        if (Number.isFinite(id)) return this.userNameById(id);
        return part;
      });
      return names.join(", ");
    },
    isSubTasksHistoryField(fieldName) {
      return String(fieldName || "").trim() === "Sub Tasks";
    },
    parseSubTasksHistoryJson(value) {
      if (value == null || value === "" || value === "—" || value === "[]") return [];
      try {
        const parsed = JSON.parse(String(value));
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [];
      }
    },
    subTaskHistoryAssigneeLabel(st) {
      const id = st?.assigneeUserId;
      if (id == null || id === "") return "—";
      const name = this.userNameById(Number(id));
      return name && name !== "—" ? name : "—";
    },
    async onListTaskStatusChange(item, status) {
      const next = status == null ? "" : status;
      if (!item || item.testRunId) return;
      if (String(item.testPlanStatus || "") === String(next)) return;
      const prev = item.testPlanStatus;
      item.testPlanStatus = next;
      try {
        await testPlanItemsApi.update(item.testPlanId, {
          status: next,
          actorUserId: this.currentUserId,
        });
        await this.updateParentTestPlanStatus();
      } catch (e) {
        item.testPlanStatus = prev;
        console.error("update task status error:", e);
        alert(e.message || "Update status failed");
      }
    },
    async onListSubTaskStatusChange(item, subIndex, status) {
      const next = status == null ? "" : status;
      const list = this.normalizeSubTasks(item?.subTasks).map((subTask) => ({ ...subTask }));
      if (!list[subIndex]) return;
      if (String(list[subIndex].status || "") === String(next)) return;
      list[subIndex].status = next;
      try {
        await testPlanItemsApi.update(item.testPlanId, {
          testPlanName: item.testPlanName,
          planOverallStart: item.planOverallStart || null,
          planOverallDue: item.planOverallDue || null,
          actionStartDate: item.actionStartDate || null,
          actionDueDate: item.actionDueDate || null,
          userIds: (item.qaOwners || []).map((owner) => Number(owner.userId)),
          status: item.testPlanStatus || "to_do",
          details: item.details || null,
          ownerType: this.taskOwnerType(item) || null,
          subTasks: list,
          actorUserId: this.currentUserId,
        });
        item.subTasks = list;
        await this.updateParentTestPlanStatus();
      } catch (e) {
        console.error("update sub task status error:", e);
        alert(e.message || "Update status failed");
        await this.loadItems();
      }
    },
    async onModalStatusChange(status) {
      const next = status == null ? "" : status;
      this.form.status = next;
      if (this.viewOnly) return;
      if (!this.formReadonly || this.isStatusFromTestRun) return;
      try {
        if (this.subTaskDetailContext) {
          const { parentItem, subIndex } = this.subTaskDetailContext;
          const list = this.normalizeSubTasks(parentItem?.subTasks).map((subTask) => ({ ...subTask }));
          if (!list[subIndex]) return;
          list[subIndex].status = next;
          await testPlanItemsApi.update(parentItem.testPlanId, {
            testPlanName: parentItem.testPlanName,
            planOverallStart: parentItem.planOverallStart || null,
            planOverallDue: parentItem.planOverallDue || null,
            actionStartDate: parentItem.actionStartDate || null,
            actionDueDate: parentItem.actionDueDate || null,
            userIds: (parentItem.qaOwners || []).map((owner) => Number(owner.userId)),
            status: parentItem.testPlanStatus || "to_do",
            details: parentItem.details || null,
            ownerType: this.taskOwnerType(parentItem) || null,
            subTasks: list,
            actorUserId: this.currentUserId,
          });
          parentItem.subTasks = list;
          const row = this.items.find((entry) => entry.testPlanId === parentItem.testPlanId);
          if (row) row.subTasks = list;
        } else if (this.editItem) {
          await testPlanItemsApi.update(this.editItem.testPlanId, {
            status: next,
            actorUserId: this.currentUserId,
          });
          this.editItem.testPlanStatus = next;
          const row = this.items.find((entry) => entry.testPlanId === this.editItem.testPlanId);
          if (row) row.testPlanStatus = next;
          if (this.editBaseline) this.editBaseline.status = next;
        }
        await this.updateParentTestPlanStatus();
      } catch (e) {
        console.error("update modal status error:", e);
        this.saveError = e.message || "Update status failed";
      }
    },
    statusLabel: testPlanTaskStatusLabel,
    statusPillClass: testPlanTaskStatusPillClass,
    computeAutoStatus(items) {
      if (items.length === 0) return null;
      const normalize = (v) => String(v || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
      const ss = items.map((i) => normalize(i.testPlanStatus));

      if (ss.every((s) => s === "to_do")) return "to_do";
      if (ss.every((s) => s === "done")) return "ready_to_deploy";
      if (!ss.some((s) => s === "to_do") && ss.some((s) => s === "blocked" || s === "block")) return "blocked";
      if (!ss.some((s) => s === "to_do") && ss.some((s) => s === "hold")) return "hold";

      return "in_progress";
    },
    async updateParentTestPlanStatus() {
      const autoStatus = this.computeAutoStatus(this.items);
      if (!autoStatus) return;
      try {
        await fetchWithTimeout(`${API_BASE_URL}/api/test-plans/by-project-plan/${this.projectPlanId()}/status`, {
          method: "PUT",
          headers: apiHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({ status: autoStatus, actorUserId: this.currentUserId }),
        });
      } catch (e) {
        console.warn('auto-update parent test plan status failed:', e.message);
      }
    },
    buildEditUpdatePayload() {
      const b = this.editBaseline;
      const f = this.form;
      if (!b) return null;
      const payload = { actorUserId: this.currentUserId };
      const norm = (v) => String(v ?? "").trim();
      if (norm(f.testPlanName) !== norm(b.testPlanName)) {
        payload.testPlanName = f.testPlanName.trim();
      }
      if (norm(f.planOverallStart) !== norm(b.planOverallStart)) {
        payload.planOverallStart = f.planOverallStart || null;
      }
      if (norm(f.planOverallDue) !== norm(b.planOverallDue)) {
        payload.planOverallDue = f.planOverallDue || null;
      }
      const userIds = [...(f.userIds || [])].map(Number).filter(Number.isFinite).sort((a, c) => a - c);
      const baseIds = [...(b.userIds || [])].sort((a, c) => a - c);
      if (userIds.join(",") !== baseIds.join(",")) {
        payload.userIds = userIds;
      }
      if (norm(f.ownerType) !== norm(b.ownerType)) {
        payload.ownerType = f.ownerType || null;
      }
      if (norm(f.status) !== norm(b.status) && !this.isStatusFromTestRun) {
        payload.status = f.status;
      }
      if (Number(f.testRunId || 0) !== Number(b.testRunId || 0)) {
        payload.testRunId = f.testRunId || null;
      }
      if (norm(f.details) !== norm(b.details)) {
        payload.details = f.details || null;
      }
      const subTasks = this.serializeSubTasksForSave(f.subTasks);
      if (JSON.stringify(subTasks) !== b.subTasksJson) {
        payload.subTasks = subTasks;
      }
      const taskJiraLinksJson = JSON.stringify(f.taskJiraLinks || []);
      if (taskJiraLinksJson !== (b.taskJiraLinksJson || "[]")) {
        payload.taskLink = this.serializeTaskJiraLinks(f.taskJiraLinks);
      }
      return payload;
    },
    async saveSubTaskFromMainModal() {
      if (!this.subTaskDetailContext) return;
      const { parentItem, subIndex } = this.subTaskDetailContext;
      const list = this.normalizeSubTasks(parentItem?.subTasks);
      if (!list[subIndex]) return;
      this.isSaving = true;
      this.saveError = null;
      try {
        const assigneeUserId = Number.isFinite(Number(this.form.userIds?.[0]))
          ? Number(this.form.userIds[0])
          : null;
        list[subIndex] = {
          ...list[subIndex],
          name: this.form.testPlanName.trim(),
          detail: this.form.details || "",
          startDate: this.form.planOverallStart || null,
          dueDate: this.form.planOverallDue || null,
          status: this.form.status || "to_do",
          assigneeUserId,
          jiraLinks: [...(this.form.taskJiraLinks || [])],
          jiraLink: this.serializeTaskJiraLinks(this.form.taskJiraLinks),
        };
        await testPlanItemsApi.update(parentItem.testPlanId, {
          testPlanName: parentItem.testPlanName,
          planOverallStart: parentItem.planOverallStart || null,
          planOverallDue: parentItem.planOverallDue || null,
          actionStartDate: parentItem.actionStartDate || null,
          actionDueDate: parentItem.actionDueDate || null,
          userIds: (parentItem.qaOwners || []).map((owner) => Number(owner.userId)),
          status: parentItem.testPlanStatus || "to_do",
          details: parentItem.details || null,
          ownerType: this.taskOwnerType(parentItem) || null,
          subTasks: list,
          actorUserId: this.currentUserId,
        });
        await this.loadItems();
        await this.updateParentTestPlanStatus();
        this.closeModal();
      } catch (e) {
        console.error("save sub task from modal error:", e);
        this.saveError = e.message || "Save sub task failed";
      } finally {
        this.isSaving = false;
      }
    },
    async save() {
      if (!this.form.testPlanName.trim()) return;
      if (this.isSaving) return;
      if (this.subTaskDetailContext) {
        await this.saveSubTaskFromMainModal();
        return;
      }
      this.isSaving = true;
      this.saveError = null;
      try {
        if (this.editItem) {
          const payload = this.buildEditUpdatePayload();
          if (!payload || Object.keys(payload).length <= 1) {
            this.closeModal();
            return;
          }
          await testPlanItemsApi.update(this.editItem.testPlanId, payload);
          await this.loadItems();
          await this.updateParentTestPlanStatus();
          this.closeModal();
        } else {
          const payload = {
            testPlanName: this.form.testPlanName.trim(),
            planOverallStart: this.form.planOverallStart || null,
            planOverallDue: this.form.planOverallDue || null,
            actionStartDate: null,
            actionDueDate: null,
            userIds: this.form.userIds,
            ownerType: this.form.ownerType || "qa",
            status: this.form.status,
            details: this.form.details || null,
            subTasks: this.serializeSubTasksForSave(this.form.subTasks),
            taskLink: this.serializeTaskJiraLinks(this.form.taskJiraLinks),
            testRunId: this.form.testRunId || null,
          };
          await testPlanItemsApi.insert({
            projectPlanId: this.projectPlanId(),
            ...payload,
            createJiraSubtask: false,
            jiraIssueKeys: [],
            appBaseUrl: window.location.origin,
          });
          await this.loadItems();
          await this.updateParentTestPlanStatus();
          this.closeModal();
        }
      } catch (e) {
        console.error("save error:", e);
        this.saveError = e.message || "Save failed. Please try again.";
      } finally {
        this.isSaving = false;
      }
    },
    async loadJiraProjects() {
      if (this.jiraProjects.length > 0) return;
      this.jiraLoadingProjects = true;
      try {
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/projects`);
        this.jiraProjects = await res.json();
      } catch (e) { console.error("loadJiraProjects error:", e); }
      finally { this.jiraLoadingProjects = false; }
    },
    async loadJiraIssues(projectKey, search = "") {
      this.jiraLoadingIssues = true;
      try {
        const q = search ? `&search=${encodeURIComponent(search)}` : "";
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/issues?projectKey=${projectKey}${q}`);
        this.jiraIssues = await res.json();
      } catch (e) { console.error("loadJiraIssues error:", e); }
      finally { this.jiraLoadingIssues = false; }
    },
    subTaskJiraContext(index) {
      return `subtask:${index}`;
    },
    parseJiraContext(context) {
      if (context === "task") return { type: "task", index: null };
      const m = String(context || "").match(/^subtask:(\d+)$/);
      if (m) return { type: "subtask", index: Number(m[1]) };
      return { type: "task", index: null };
    },
    getSubTaskJiraLinks(subTask) {
      if (Array.isArray(subTask?.jiraLinks)) return subTask.jiraLinks;
      return this.parseTaskJiraLinksRaw(subTask?.jiraLink);
    },
    getJiraLinksRef(context) {
      const parsed = this.parseJiraContext(context);
      if (parsed.type === "task") return this.form.taskJiraLinks;
      const subTask = this.form.subTasks[parsed.index];
      if (!subTask) return null;
      if (!Array.isArray(subTask.jiraLinks)) subTask.jiraLinks = this.getSubTaskJiraLinks(subTask);
      return subTask.jiraLinks;
    },
    scopeJiraProjectKeyFrom(links) {
      const k = (links || [])[0]?.key;
      return k ? String(k).split("-")[0] : "";
    },
    jiraBrowseBase() {
      const url = this.form.taskJiraLinks[0]?.url
        || this.subProjectJiraLinks[0]?.url
        || "";
      const m = String(url).match(/^(https?:\/\/[^/]+)\/browse/i);
      if (m) return `${m[1]}/browse`;
      return "https://lightnet-thailand.atlassian.net/browse";
    },
    parseTaskJiraLinksRaw(raw) {
      return parseJiraLinks(raw).map((l) => {
        const key = String(l.key || "").trim().toUpperCase();
        return {
          key,
          url: l.url || `${this.jiraBrowseBase()}/${key}`,
          summary: l.summary || "",
          status: l.status || "",
          transitions: l.transitions || [],
        };
      }).filter((l) => l.key);
    },
    serializeTaskJiraLinks(links) {
      const list = (Array.isArray(links) ? links : []).filter((l) => l?.key);
      if (!list.length) return null;
      const base = this.jiraBrowseBase();
      return JSON.stringify(
        list.map((l) => ({
          key: String(l.key).trim().toUpperCase(),
          url: l.url || `${base}/${l.key}`,
        }))
      );
    },
    mergeTaskJiraLinks(target, keys) {
      const base = this.jiraBrowseBase();
      const byKey = new Map((target || []).map((l) => [String(l.key).toUpperCase(), l]));
      for (const key of keys) {
        const k = String(key || "").trim().toUpperCase();
        if (!k || byKey.has(k)) continue;
        byKey.set(k, { key: k, url: `${base}/${k}`, summary: "", status: "", transitions: [] });
      }
      return [...byKey.values()];
    },
    async enrichAllModalJiraLinks() {
      const keys = this.modalJiraRows.map((r) => r.key).filter(Boolean);
      if (!keys.length) return;
      this.modalJiraLinksLoading = true;
      try {
        const data = await fetchJiraIssuesByKeys(keys);
        const enrich = (links) => enrichJiraLinkRows(links, data);
        this.form.taskJiraLinks = enrich(this.form.taskJiraLinks || []);
        (this.form.subTasks || []).forEach((st) => {
          if (!Array.isArray(st.jiraLinks) || !st.jiraLinks.length) {
            st.jiraLinks = this.getSubTaskJiraLinks(st);
          }
          if (st.jiraLinks.length) {
            st.jiraLinks = enrich(st.jiraLinks);
          }
        });
      } catch (_) { /* ignore */ }
      finally { this.modalJiraLinksLoading = false; }
    },
    openJiraActionModal(mode, context) {
      this.jiraActionMode = mode;
      this.jiraActionContext = context;
      this.jiraActionError = null;
      this.jiraCreateError = null;
      this.jiraActionIssueKeys = [];
      this.jiraActionSearch = "";
      this.jiraIssues = [];
      this.jiraActionAssigneeAccountId = this.currentUser?.jiraAccountId || "";
      this.jiraActionProjectKey = this.scopeJiraProjectKeyFrom(this.subProjectJiraLinks) || "";
      const parsed = this.parseJiraContext(context);
      const defaultTitle = parsed.type === "subtask"
        ? (this.form.subTasks[parsed.index]?.name || "").trim()
        : (this.form.testPlanName || "").trim();
      const defaultDetails = parsed.type === "subtask"
        ? (this.form.subTasks[parsed.index]?.detail || "").trim()
        : (this.form.details || "").trim();
      this.jiraActionTitle = defaultTitle;
      this.jiraActionDescription = defaultDetails || defaultTitle;
      this.showJiraActionModal = true;
      this.loadJiraProjects().then(() => {
        if (this.jiraActionProjectKey) this.onJiraActionProjectChange();
      });
    },
    closeJiraActionModal() {
      if (this.jiraCreatingIssue) return;
      this.showJiraActionModal = false;
      this.jiraActionMode = null;
      this.jiraActionContext = null;
      this.jiraActionIssueKeys = [];
      this.jiraActionError = null;
      this.jiraActionTitle = "";
      this.jiraActionDescription = "";
      this.jiraActionAssigneeAccountId = "";
      this.jiraAssignableUsers = [];
    },
    onJiraActionProjectChange() {
      this.jiraActionIssueKeys = [];
      this.jiraIssues = [];
      this.jiraActionSearch = "";
      if (this.jiraActionProjectKey) {
        this.loadJiraIssues(this.jiraActionProjectKey);
        this.loadJiraAssignableUsers();
      } else {
        this.jiraAssignableUsers = [];
      }
    },
    async loadJiraAssignableUsers() {
      const pk = this.jiraActionProjectKey;
      if (!pk) {
        this.jiraAssignableUsers = [];
        return;
      }
      this.jiraLoadingAssignable = true;
      try {
        const params = new URLSearchParams({ projectKey: pk });
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/assignable-users?${params.toString()}`, {
          headers: apiHeaders(),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(typeof data.error === "string" ? data.error : "Failed to load assignable users");
        this.jiraAssignableUsers = Array.isArray(data) ? data : [];
      } catch (e) {
        console.error("loadJiraAssignableUsers:", e);
        this.jiraAssignableUsers = [];
      } finally {
        this.jiraLoadingAssignable = false;
      }
    },
    toggleJiraActionTicket(key) {
      const i = this.jiraActionIssueKeys.indexOf(key);
      if (this.jiraActionMode === "link") {
        if (i === -1) this.jiraActionIssueKeys.push(key);
        else this.jiraActionIssueKeys.splice(i, 1);
        return;
      }
      this.jiraActionIssueKeys = i === -1 ? [key] : [];
    },
    removeJiraActionTicket(key) {
      this.jiraActionIssueKeys = this.jiraActionIssueKeys.filter((k) => k !== key);
    },
    onJiraActionSearch() {
      clearTimeout(this.jiraActionSearchTimer);
      this.jiraActionSearchTimer = setTimeout(() => {
        if (this.jiraActionProjectKey) {
          this.loadJiraIssues(this.jiraActionProjectKey, this.jiraActionSearch);
        }
      }, 400);
    },
    async confirmJiraAction() {
      if (this.jiraActionConfirmDisabled) return;
      this.jiraActionError = null;
      const context = this.jiraActionContext;
      const mode = this.jiraActionMode;
      const linksRef = this.getJiraLinksRef(context);
      if (!linksRef) return;
      try {
        if (mode === "link") {
          const keys = [...this.jiraActionIssueKeys];
          const merged = this.mergeTaskJiraLinks([...(linksRef || [])], keys);
          const parsed = this.parseJiraContext(context);
          try {
            const data = await fetchJiraIssuesByKeys(merged.map((l) => l.key));
            const enriched = enrichJiraLinkRows(merged, data);
            if (parsed.type === "task") this.form.taskJiraLinks = enriched;
            else if (this.form.subTasks[parsed.index]) this.form.subTasks[parsed.index].jiraLinks = enriched;
          } catch (_) {
            if (parsed.type === "task") this.form.taskJiraLinks = merged;
            else if (this.form.subTasks[parsed.index]) this.form.subTasks[parsed.index].jiraLinks = merged;
          }
          this.closeJiraActionModal();
          return;
        }
        const title = String(this.jiraActionTitle || "").trim();
        const description = String(this.jiraActionDescription || "").trim();
        this.jiraCreatingIssue = true;
        const res = await fetchWithTimeout(`${API_BASE_URL}/api/jira/create-issue`, {
          method: "POST",
          headers: apiHeaders({ "Content-Type": "application/json" }),
          body: JSON.stringify({
            title,
            description,
            projectKey: this.jiraActionProjectKey,
            standaloneMode: true,
            issueTypePreference: "task",
            linkedIssueKeys: [...this.jiraActionIssueKeys],
            assigneeAccountId: this.jiraActionAssigneeAccountId || null,
            reporterAccountId: this.currentUser?.jiraAccountId || null,
            appBaseUrl: typeof window !== "undefined" ? window.location.origin : null,
          }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(typeof json.error === "string" ? json.error : "Failed to create Jira issue");
        const createdKey = json.key;
        if (!createdKey) throw new Error("Jira issue created but key missing");
        let merged = this.mergeTaskJiraLinks([...(linksRef || [])], [createdKey]);
        try {
          const data = await fetchJiraIssuesByKeys([createdKey]);
          merged = enrichJiraLinkRows(merged, data);
        } catch (_) { /* ignore */ }
        const parsed = this.parseJiraContext(context);
        if (parsed.type === "task") this.form.taskJiraLinks = merged;
        else if (this.form.subTasks[parsed.index]) this.form.subTasks[parsed.index].jiraLinks = merged;
        this.closeJiraActionModal();
      } catch (e) {
        this.jiraActionError = e?.message || "Jira action failed";
      } finally {
        this.jiraCreatingIssue = false;
      }
    },
    removeModalJiraLink(row) {
      const k = String(row?.key || "").trim().toUpperCase();
      if (!k) return;
      if (row.sourceType === "task") {
        this.form.taskJiraLinks = (this.form.taskJiraLinks || []).filter((l) => String(l.key).toUpperCase() !== k);
        return;
      }
      const subTask = this.form.subTasks[row.sourceIndex];
      if (!subTask) return;
      subTask.jiraLinks = this.getSubTaskJiraLinks(subTask).filter((l) => String(l.key).toUpperCase() !== k);
    },
    removeSubTaskJiraLink(subTaskIndex, key) {
      this.removeModalJiraLink({ sourceType: "subtask", sourceIndex: subTaskIndex, key });
    },

    async remove(item) {
      if (!confirm(`Delete "${item.testPlanName}"?`)) return;
      try {
        await testPlanItemsApi.delete(item.testPlanId);
        await this.loadItems();
      } catch (e) {
        console.error("delete error:", e);
        alert(e.message || "Delete failed");
      }
    },
  },
};
</script>

<style scoped>
.plan-table {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  overflow: hidden;
}
.plan-table-header {
  display: flex;
  align-items: center;
  padding: 0.65rem 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  font-size: 0.7rem;
  font-weight: 700;
  color: #8392ab;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.plan-row {
  display: flex;
  align-items: center;
  padding: 0.9rem 1rem;
  transition: background 0.15s;
}
.plan-row:hover { background: #fafbfc; }
.task-group {
  border-bottom: 1px solid #f1f3f5;
}
.task-group:last-child {
  border-bottom: none;
}
.subtask-row {
  display: flex;
  align-items: center;
  padding: 0.45rem 1rem;
  background: #ffffff;
}

.col-name     { flex: 3.3; min-width: 0; }
.col-sub-task { flex: 0.8; min-width: 80px; }
.col-date     { flex: 1.1; min-width: 90px; }
.col-status   { flex: 1.35; min-width: 150px; }
.col-status :deep(.base-select) {
  width: 100%;
  text-align: left;
}
.col-action   { flex: 0.8; min-width: 70px; }

.plan-name { font-size: 0.875rem; font-weight: 700; color: #344767; }
.plan-name-link:hover { color: #cb0c9f; text-decoration: underline; }
.plan-sub  { font-size: 0.78rem; color: #8392ab; margin-top: 2px; }
.task-type-prefix { color: #006699; font-weight: 700; }
.subtask-name {
  color: #344767;
  font-size: 0.85rem;
  font-weight: 600;
}
.subtask-name-link:hover { color: #cb0c9f; text-decoration: underline; }
.subtask-name-wrap { margin-left: 5ch; }
.subtask-row .col-date,
.subtask-row .col-status,
.subtask-row .col-action {
  padding-left: 0;
}
.assignee-badge {
  font-size: 0.7rem !important;
  padding: 0.04rem 0.26rem !important;
  line-height: 1.05;
  font-weight: 500;
}
.tp-run-link {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 600;
  color: #67748e;
  background: #f1f3f5;
  border-radius: 999px;
  padding: 0.04rem 0.4rem;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.subtask-assignee-badge {
  font-size: 0.7rem !important;
  padding: 0.03rem 0.2rem !important;
  line-height: 1;
  font-weight: 500;
}

/* QA badges */
.qa-badge {
  display: inline-block;
  background: #e8f4fd;
  color: #0c7a9a;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.15rem 0.55rem;
}

.bo-assignee-pill {
  background: #e8f4fd !important;
  color: #0c7a9a !important;
  border-color: #bee5eb !important;
}

/* Action buttons */
.action-edit   { color: #4361ee; }
.action-edit:hover { color: #2d4bc8; }
.action-delete { color: #e74c3c; }
.action-delete:hover { color: #c0392b; }

/* Jira picker */
.jira-tag {
  display: inline-flex;
  align-items: center;
  background: #e8f4fd;
  color: #0c7a9a;
  border: 1px solid #bee5eb;
  border-radius: 50px;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 8px;
  margin: 2px 2px 2px 0;
}
.jira-tag-remove {
  background: none;
  border: none;
  color: #0c7a9a;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 0 0 0 4px;
  line-height: 1;
}
.jira-issue-list {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 6px;
}
.jira-issue-item {
  padding: 6px 10px;
  cursor: pointer;
  font-size: 0.75rem;
  border-bottom: 1px solid #f0f0f0;
  transition: background 0.15s;
}
.jira-issue-item:last-child { border-bottom: none; }
.jira-issue-item:hover { background: #f8f9fa; }
.jira-issue-selected { background: #e8f4fd; }
.jira-issue-list--plain .jira-issue-item { cursor: pointer; }
.jira-issue-list--plain .jira-issue-selected { background: #e8f4fd; font-weight: 600; }

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.modal-box {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  width: 480px;
  max-width: 95vw;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-box-edit {
  width: 1220px;
  max-width: 98vw;
  height: 65vh;
  max-height: 65vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-box-edit > .border-bottom,
.modal-box-edit > .p-3.border-top {
  flex-shrink: 0;
}

.modal-split-body {
  display: flex;
  align-items: stretch;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.modal-pane-form {
  flex: 1;
  min-width: 0;
  min-height: 0;
  padding: 1rem;
  padding-bottom: 1.25rem;
  overflow-x: hidden;
  overflow-y: auto;
  border-right: 1px solid #e9ecef;
  -webkit-overflow-scrolling: touch;
}

.modal-box-edit--simple {
  width: 560px;
  height: auto;
  max-height: 85vh;
}

.modal-box-edit--simple .modal-pane-form {
  border-right: none;
}

.tp-subtask-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tp-subtask-row__name {
  flex: 1.2;
  min-width: 0;
}

.tp-subtask-row__assignee {
  flex: 1;
  min-width: 0;
}

.tp-subtask-row__remove {
  flex-shrink: 0;
  width: 1.5rem;
  line-height: 1;
}

.modal-pane-form :deep(.base-select--open) {
  position: relative;
  z-index: 20;
}

.tp-modal-side-panel {
  width: 560px;
  flex-shrink: 0;
  min-height: 0;
  padding: 0.75rem 1rem 1rem;
  overflow: hidden;
  background: #f8f9fa;
  display: flex;
  flex-direction: column;
  border-left: 1px solid #e9ecef;
}

.tp-side-tabs {
  display: flex;
  gap: 0.35rem;
  margin-bottom: 0.75rem;
  flex-shrink: 0;
}

.tp-side-tab {
  flex: 1;
  border: 1px solid #e9ecef;
  background: #fff;
  color: #67748e;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.45rem 0.55rem;
  border-radius: 0.45rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.tp-side-tab.active {
  background: #344767;
  border-color: #344767;
  color: #fff;
}

.tp-side-tab-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tp-side-tab-panel--remark {
  overflow: hidden;
}

.tp-side-tab-panel--remark .modal-remark-history {
  flex: 1;
  min-height: 0;
}

.tp-jira-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.tp-jira-actions .btn {
  font-size: 0.68rem;
  padding: 0.2rem 0.45rem;
  line-height: 1.2;
}

.tp-jira-table-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.tp-jira-table {
  width: 100%;
  table-layout: fixed;
}

.tp-jira-table th {
  font-size: 0.65rem;
  text-transform: uppercase;
  color: #8392ab;
  padding: 0.35rem 0.4rem;
  border-bottom: 1px solid #e9ecef;
}

.tp-jira-table td {
  padding: 0.4rem;
  vertical-align: middle;
  border-color: #f0f2f5;
  font-size: 0.75rem;
}

.tp-jira-col-id { width: 16%; white-space: nowrap; }
.tp-jira-col-title {
  width: 38%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tp-jira-col-source {
  width: 18%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tp-jira-col-status { width: 22%; }
.tp-jira-col-action { width: 6%; }

.tp-jira-col-status :deep(.jira-status-select--mini) {
  max-width: 100%;
}

.modal-remark-fixed {
  flex-shrink: 0;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 0.75rem;
}

.modal-remark-history {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.activity-history-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 2px;
}

.activity-history-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.activity-history-item {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.55rem 0.65rem;
}

.activity-badge {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.1rem 0.4rem;
  border-radius: 4px;
}

.badge-remark {
  background: #e8f4fd;
  color: #0c7a9a;
}

.badge-change {
  background: #fff3cd;
  color: #856404;
}

.activity-text {
  white-space: pre-wrap;
  word-break: break-word;
  color: #344767;
}

.activity-subtasks-history {
  color: #344767;
}

.activity-subtasks-table {
  font-size: 0.72rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.activity-subtasks-table thead th {
  background: #f8f9fa;
  color: #8392ab;
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  padding: 0.35rem 0.5rem;
  border-bottom: 1px solid #e9ecef;
  white-space: nowrap;
}

.activity-subtasks-table tbody td {
  padding: 0.4rem 0.5rem;
  vertical-align: top;
  color: #344767;
  border-bottom: 1px solid #f1f3f5;
}

.activity-subtasks-table tbody tr:last-child td {
  border-bottom: none;
}

.modal-box-detail {
  width: 560px;
  max-width: 96vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-detail-body {
  overflow-y: auto;
  max-height: calc(90vh - 110px);
}

.detail-text-block {
  white-space: pre-wrap;
  word-break: break-word;
  color: #344767;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.65rem 0.75rem;
  min-height: 2.5rem;
}

.modal-detail-remarks {
  border-top: 1px solid #e9ecef;
  padding-top: 0.75rem;
}

.detail-remark-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  max-height: 220px;
  overflow-y: auto;
  padding-right: 2px;
}

.btn-modal-close {
  color: #b22222 !important;
  border-color: #b22222 !important;
  background-color: transparent !important;
}

.btn-modal-close:hover,
.btn-modal-close:focus {
  color: #b22222 !important;
  background-color: #e1c5c5 !important;
  border-color: #b22222 !important;
}
</style>
